import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { ArrowLeft, FileText, ExternalLink, AlertCircle, CheckCircle, Clock, ImageIcon, QrCode, Upload } from 'lucide-react';
import TicketUploadModal from './TicketUploadModal';
import { useAuth } from '../App';

const UPLOAD_ROLES = ['super_admin', 'admin', 'office_admin', 'hr', 'project_manager', 'project_manager_estimator'];

const HREmployeeDetail = () => {
  const { employeeEmail } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [employee, setEmployee] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const myRole = me?.role;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [thumbnails, setThumbnails] = useState({});
  const [displayLimit, setDisplayLimit] = useState(10);
  const [qrBusy, setQrBusy] = useState(false);
  const abortRef = useRef(null);
  const requestedRef = useRef(new Set());

  const printQrSticker = async () => {
    setQrBusy(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/qr-stickers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ base_url: window.location.origin, emails: [employeeEmail] })
      });
      if (!response.ok) throw new Error(response.status === 404 ? 'No user account found for this employee' : 'Could not generate the QR sticker');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `QR Sticker - ${employee?.employee_name || employeeEmail}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      alert(err.message);
    } finally {
      setQrBusy(false);
    }
  };

  useEffect(() => {
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    requestedRef.current = new Set();
    setThumbnails({});
    setDisplayLimit(10);
    fetchEmployeeDetails(ctrl.signal);
    return () => ctrl.abort();
  }, [employeeEmail]);

  const fetchEmployeeDetails = async (signal) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/hr/certificates-overview`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }, signal }
      );
      if (!response.ok) throw new Error('Failed to fetch employee details');
      const data = await response.json();
      const employeeData = data.employees.find(emp => emp.employee_email === employeeEmail);
      if (!employeeData) throw new Error('Employee not found');
      setEmployee(employeeData);
      setCertificates(employeeData.certificates || []);
      setLoading(false);
    } catch (err) {
      if (err.name === 'AbortError') return;
      setError(err.message);
      setLoading(false);
    }
  };

  // Load thumbnails ONLY for visible cards, max 3 at a time, aborted on unmount
  useEffect(() => {
    const signal = abortRef.current?.signal;
    const queue = certificates
      .slice(0, displayLimit)
      .map(c => c.google_drive_file_id)
      .filter(id => id && !requestedRef.current.has(id));
    if (queue.length === 0) return;
    queue.forEach(id => requestedRef.current.add(id));

    const loadOne = async (fileId) => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/certificate-thumbnail/${fileId}`,
          { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }, signal }
        );
        if (!response.ok) return;
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setThumbnails(prev => ({ ...prev, [fileId]: url }));
      } catch (err) { /* aborted or failed — placeholder stays */ }
    };

    const workers = Array.from({ length: 3 }, async () => {
      while (queue.length > 0 && !(signal && signal.aborted)) {
        await loadOne(queue.shift());
      }
    });
    Promise.all(workers);
  }, [certificates, displayLimit]);

  const stats = useMemo(() => {
    const ren = employee?.renewals || [];
    const expired = ren.filter(r => r.status === 'expired').length;
    const expiring = ren.length - expired;
    const hidden = certificates.filter(c => c.expiry_status === 'superseded' || c.expiry_status === 'duplicate').length;
    return { total: certificates.length, expired, expiring, hidden, active: certificates.filter(c => c.expiry_status === 'active' || c.expiry_status === 'no_expiry').length };
  }, [certificates, employee]);

  // renewals first, then current cards, superseded / duplicate re-scans last
  const sortedCerts = useMemo(() => {
    const rank = { expired: 0, expiring_30: 1, expiring_60: 2, expiring_90: 3, active: 4, no_expiry: 5, superseded: 6, duplicate: 7 };
    return [...certificates].sort((a, b) => (rank[a.expiry_status] ?? 5) - (rank[b.expiry_status] ?? 5) || String(a.effective_expiry || '').localeCompare(String(b.effective_expiry || '')));
  }, [certificates]);

  const openFullCertificate = async (cert) => {
    if (cert.google_drive_file_id) {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/certificate-file/${cert.google_drive_file_id}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          window.open(url, '_blank');
          setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        }
      } catch (err) {
        console.error('Error opening certificate:', err);
      }
    } else if (cert.certificate_file_url) {
      window.open(cert.certificate_file_url, '_blank');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      expired: { color: 'bg-red-100 text-red-800', icon: AlertCircle, text: 'Expired' },
      expiring_30: { color: 'bg-orange-100 text-orange-800', icon: Clock, text: 'Expires ≤ 30 days' },
      expiring_60: { color: 'bg-amber-100 text-amber-800', icon: Clock, text: 'Expires ≤ 60 days' },
      expiring_soon: { color: 'bg-orange-100 text-orange-800', icon: Clock, text: 'Expiring Soon' },
      expiring_90: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, text: 'Expires ≤ 90 days' },
      active: { color: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Active' },
      superseded: { color: 'bg-gray-100 text-gray-600', icon: CheckCircle, text: 'Replaced by newer card' },
      duplicate: { color: 'bg-gray-100 text-gray-500', icon: ImageIcon, text: 'Duplicate scan' },
      no_expiry: { color: 'bg-gray-100 text-gray-800', icon: Clock, text: 'No Expiry Set' }
    };
    const badge = badges[status] || badges.no_expiry;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {badge.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading employee details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button onClick={() => navigate('/safety-tickets')} className="mt-4" data-testid="employee-detail-back-error">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Employee Certifications
        </Button>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="outline"
            onClick={() => navigate('/safety-tickets')}
            data-testid="employee-detail-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Employee Certifications
          </Button>
          <div className="flex items-center gap-2">
            {UPLOAD_ROLES.includes(myRole) && employee?.employee_id && (
              <Button onClick={() => setUploadOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="employee-detail-upload-btn" title="Upload one card or certificate straight into this person's Drive folder">
                <Upload className="h-4 w-4 mr-2" /> Upload a ticket
              </Button>
            )}
            <Button
              variant="outline"
              onClick={printQrSticker}
              disabled={qrBusy}
              data-testid="employee-detail-qr-btn"
              title="Print this person's hardhat QR sticker"
            >
              <QrCode className="h-4 w-4 mr-2" />
              {qrBusy ? 'Generating…' : 'Print QR Sticker'}
            </Button>
          </div>
        </div>
        {uploadOpen && <TicketUploadModal employeeId={employee.employee_id} employeeName={employee.employee_name} onClose={() => setUploadOpen(false)}
          onDone={() => { setUploadOpen(false); fetchEmployeeDetails(); }} />}

        <div className="bg-white rounded-lg shadow-md p-6">
          <h1 className="text-3xl font-bold text-gray-900">{employee?.employee_name}</h1>
          <p className="text-gray-600 mt-1">{employee?.employee_email}</p>

          {/* Stats */}
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-3">
              <p className="text-2xl font-bold text-blue-700" data-testid="employee-stat-total">{stats.total}</p>
              <p className="text-sm text-gray-600">Total Certificates</p>
            </div>
            <div className="bg-green-50 rounded-lg p-3">
              <p className="text-2xl font-bold text-green-700" data-testid="employee-stat-active">{stats.active}</p>
              <p className="text-sm text-gray-600">Active</p>
            </div>
            <div className="bg-orange-50 rounded-lg p-3">
              <p className="text-2xl font-bold text-orange-700" data-testid="employee-stat-expiring">{stats.expiring}</p>
              <p className="text-sm text-gray-600">Expiring Soon</p>
            </div>
            <div className="bg-red-50 rounded-lg p-3">
              <p className="text-2xl font-bold text-red-700" data-testid="employee-stat-expired">{stats.expired}</p>
              <p className="text-sm text-gray-600">Expired</p>
            </div>
          </div>
          {stats.hidden > 0 && (
            <p className="mt-3 text-xs text-gray-500" data-testid="employee-hidden-note">{stats.hidden} older card{stats.hidden !== 1 ? 's' : ''} / duplicate scan{stats.hidden !== 1 ? 's' : ''} replaced by newer cards in the folder — shown greyed out at the bottom.</p>
          )}
        </div>

        {(employee?.renewals || []).length > 0 && (
          <div className="mt-4 bg-white rounded-lg shadow-md p-5 border-l-4 border-red-500" data-testid="employee-renewals">
            <h2 className="text-base md:text-lg font-bold text-gray-900 mb-2">Needs renewal ({employee.renewals.length})</h2>
            <ul className="divide-y divide-gray-100">
              {employee.renewals.map((r) => (
                <li key={r.topic} className="py-2 flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`employee-renewal-${r.topic}`}>
                  <span className="font-semibold text-gray-900">{r.label}</span>
                  <span className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 truncate max-w-[260px]" title={r.ticket}>{r.ticket}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${r.status === 'expired' ? 'bg-red-100 text-red-800' : r.status === 'missing' ? 'bg-fuchsia-100 text-fuchsia-800' : 'bg-orange-100 text-orange-800'}`}>
                      {r.status === 'missing' ? 'Never had one — required for this role' : r.status === 'expired' ? `Expired ${r.expiry || ''}` : `Expires ${r.expiry || ''}${r.days != null ? ` · ${r.days}d` : ''}`}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Certificates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sortedCerts.slice(0, displayLimit).map((cert, index) => (
          <Card key={cert.id || index} className={`hover:shadow-lg transition-shadow ${cert.expiry_status === 'superseded' || cert.expiry_status === 'duplicate' ? 'opacity-60' : ''}`} data-testid={`employee-cert-${cert.id || index}`}>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-start justify-between gap-2">
                <span className="flex-1">{cert.display_name || cert.certificate_type || cert.course_name}</span>
                {getStatusBadge(cert.expiry_status)}
              </CardTitle>
              {cert.superseded_by && (
                <p className="text-xs text-gray-500 mt-1" data-testid={`employee-cert-superseded-${cert.id || index}`}>Replaced by “{cert.superseded_by.name}”{cert.superseded_by.expiry ? ` (exp. ${String(cert.superseded_by.expiry).slice(0, 10)})` : ''}</p>
              )}
              {cert.still_needed_for && cert.still_needed_for.length > 0 && cert.covered_topics && cert.covered_topics.length > 0 && (
                <p className="text-xs text-orange-700 mt-1">Still needed for: {cert.still_needed_for.join(', ')} · {cert.covered_topics.join(', ')} already renewed</p>
              )}
            </CardHeader>
            <CardContent>
              {/* Thumbnail */}
              {cert.google_drive_file_id && thumbnails[cert.google_drive_file_id] ? (
                <div className="mb-4 rounded-lg overflow-hidden border border-gray-200">
                  <img
                    src={thumbnails[cert.google_drive_file_id]}
                    alt={cert.certificate_type}
                    className="w-full h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => openFullCertificate(cert)}
                  />
                </div>
              ) : (
                <div className="mb-4 rounded-lg overflow-hidden border border-gray-200 bg-gray-50 h-48 flex items-center justify-center">
                  <ImageIcon className="h-12 w-12 text-slate-500" />
                </div>
              )}

              {/* Details */}
              <div className="space-y-2 text-sm">
                {cert.course_name && (
                  <div>
                    <span className="font-medium text-gray-700">Course:</span>
                    <p className="text-gray-600 text-xs mt-0.5">{cert.course_name}</p>
                  </div>
                )}
                {cert.issuing_organization && (
                  <div>
                    <span className="font-medium text-gray-700">Issuer:</span>
                    <p className="text-gray-600">{cert.issuing_organization}</p>
                  </div>
                )}
                {cert.issue_date && (
                  <div>
                    <span className="font-medium text-gray-700">Issue Date:</span>
                    <p className="text-gray-600">{formatDate(cert.issue_date)}</p>
                  </div>
                )}
                <div>
                  <span className="font-medium text-gray-700">Expiry Date:</span>
                  <p className={`font-semibold ${
                    cert.expiry_status === 'expired' ? 'text-red-600' :
                    (cert.expiry_status === 'expiring_30' || cert.expiry_status === 'expiring_60' || cert.expiry_status === 'expiring_soon') ? 'text-orange-600' :
                    'text-gray-600'
                  }`}>
                    {cert.effective_expiry ? formatDate(cert.effective_expiry) : 'Not Set'}
                    {cert.expiry_source === 'name' && <span className="ml-1 text-xs font-normal text-slate-500" title="No expiry was recorded — read from the file name">(from file name)</span>}
                  </p>
                </div>
              </div>

              {/* Actions */}
              {(cert.google_drive_file_id || cert.certificate_file_url) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full mt-4"
                  onClick={() => openFullCertificate(cert)}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View Full Certificate
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Load More Button */}
      {certificates.length > displayLimit && (
        <div className="mt-6 text-center">
          <Button
            onClick={() => setDisplayLimit(prev => prev + 10)}
            variant="outline"
            className="px-6"
            data-testid="employee-detail-load-more"
          >
            Load More ({certificates.length - displayLimit} remaining)
          </Button>
        </div>
      )}

      {certificates.length === 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-slate-500 mx-auto mb-4" />
              <p className="text-gray-600">No certificates found for this employee</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default HREmployeeDetail;
